const defaults = {
  symbol: "$",
  separator: ",",
  decimal: ".",
  errorOnInvalid: false,
  precision: 2,
  pattern: "!#",
  negativePattern: "-!#",
  format,
  fromCents: false,
};

const round = (v) => {
  return Math.round(v);
};
const pow = (p) => {
  return 10 ** p;
};
const rounding = (value, increment) => {
  return round(value / increment) * increment;
};

const groupRegex = /(\d)(?=(\d{3})+\b)/g;
const vedicRegex = /(\d)(?=(\d\d)+\d\b)/g;

/**
 * Create a new instance of Currency
 * @param {number|string|Currency} value
 * @param {object} [opts]
 */
function Currency(value, opts) {
  const that = this;

  if (!(that instanceof Currency)) {
    return new Currency(value, opts);
  }

  const settings = { ...defaults, ...opts };
  const precision = pow(settings.precision);
  const v = parse(value, settings);

  that.intValue = v;
  that.value = v / precision;

  // Set default incremental value
  settings.increment = settings.increment || 1 / precision;

  // Support vedic numbering systems
  // see: https://en.wikipedia.org/wiki/Indian_numbering_system
  if (settings.useVedic) {
    settings.groups = vedicRegex;
  } else {
    settings.groups = groupRegex;
  }

  // Intended for internal usage only - subject to change
  this._settings = settings;
  this._precision = precision;
}

function parse(value, opts, useRounding = true) {
  let v = 0;
  const { decimal, errorOnInvalid, precision: decimals, fromCents } = opts;
  const precision = pow(decimals);
  const isNumber = typeof value === "number";
  const isCurrency = value instanceof Currency;

  if (isCurrency && fromCents) {
    return value.intValue;
  }

  if (isNumber || isCurrency) {
    v = isCurrency ? value.value : value;
  } else if (typeof value === "string") {
    const regex = new RegExp(`[^-\\d${decimal}]`, "g");
    const decimalString = new RegExp(`\\${decimal}`, "g");
    v = value
      .replace(/\((.*)\)/, "-$1") // allow negative e.g. (1.99)
      .replace(regex, "") // replace any non numeric values
      .replace(decimalString, "."); // convert any decimal values
  } else {
    if (errorOnInvalid) {
      throw Error("Invalid Input");
    }
    v = 0;
  }

  if (!fromCents) {
    v *= precision; // scale number to integer value
    v = v.toFixed(4); // Handle additional decimal for proper rounding.
  }

  return useRounding ? round(v) : v;
}

/**
 * Formats a Currency object
 * @param currencyObj
 * @param {object} [opts]
 */
function format(currencyObj, settings) {
  const { pattern, negativePattern, symbol, separator, decimal, groups } =
    settings;
  const split = `${currencyObj}`.replace(/^-/, "").split(".");
  const dollars = split[0];
  const cents = split[1];

  return (currencyObj.value >= 0 ? pattern : negativePattern)
    .replace("!", symbol)
    .replace(
      "#",
      dollars.replace(groups, `$1${separator}`) + (cents ? decimal + cents : "")
    );
}

Currency.prototype = {
  /**
   * Adds values together.
   * @param {number} number
   * @returns {Currency}
   */
  add(number) {
    let { intValue, _settings, _precision } = this;
    intValue += parse(number, _settings);

    return Currency(
      intValue / (_settings.fromCents ? 1 : _precision),
      _settings
    );
  },

  /**
   * Subtracts value.
   * @param {number} number
   * @returns {Currency}
   */
  subtract(number) {
    let { intValue, _settings, _precision } = this;
    intValue -= parse(number, _settings);

    return Currency(
      intValue / (_settings.fromCents ? 1 : _precision),
      _settings
    );
  },

  /**
   * Multiplies values.
   * @param {number} number
   * @returns {Currency}
   */
  multiply(number) {
    let { intValue, _settings } = this;
    intValue *= number;
    return Currency(
      intValue / (_settings.fromCents ? 1 : pow(_settings.precision)),
      _settings
    );
  },

  /**
   * Divides value.
   * @param {number} number
   * @returns {Currency}
   */
  divide(number) {
    let { intValue, _settings } = this;
    intValue /= parse(number, _settings, false);
    return Currency(intValue, _settings);
  },

  /**
   * Takes the currency amount and distributes the values evenly. Any extra pennies
   * left over from the distribution will be stacked onto the first set of entries.
   * @param {number} count
   * @returns {array}
   */
  distribute(count) {
    const { intValue, _precision, _settings } = this;
    const distribution = [];
    const split = Math[intValue >= 0 ? "floor" : "ceil"](intValue / count);
    let pennies = Math.abs(intValue - split * count);
    const precision = _settings.fromCents ? 1 : _precision;

    for (; count > 0; count--) {
      let item = Currency(split / precision, _settings);

      if (pennies > 0) {
        item = item[intValue >= 0 ? "add" : "subtract"](1 / precision);
        pennies--;
      }

      distribution.push(item);
    }

    return distribution;
  },

  /**
   * Returns the dollar value.
   * @returns {number}
   */
  dollars() {
    return ~~this.value;
  },

  /**
   * Returns the cent value.
   * @returns {number}
   */
  cents() {
    const { intValue, _precision } = this;
    return ~~(intValue % _precision);
  },

  /**
   * Formats the value as a string according to the formatting settings.
   * @param {boolean} useSymbol - format with currency symbol
   * @returns {string}
   */
  format(options) {
    const { _settings } = this;

    if (typeof options === "function") {
      return options(this, _settings);
    }

    return _settings.format(this, { ..._settings, ...options });
  },

  /**
   * Formats the value as a string according to the formatting settings.
   * @returns {string}
   */
  toString() {
    const { intValue, _precision, _settings } = this;
    return rounding(intValue / _precision, _settings.increment).toFixed(
      _settings.precision
    );
  },

  /**
   * Value for JSON serialization.
   * @returns {float}
   */
  toJSON() {
    return this.value;
  },
};

export { Currency, Currency as currency };
